import React, { useEffect } from "react";
import { animated, useSpring } from "@react-spring/web";
import _ from "lodash";
import { Page, StoryBoard } from "src/types/cleverstory";
import useMeasure from "react-use-measure";
import cx from "classnames";
import styles from "./styles.module.scss";
import dateFormat from "dateformat";
import { IKImage } from "imagekitio-react";
import { IntegraionService } from "src/utils/integration.service";
import { useDispatch } from "react-redux";
import {
  createAssetFromStoryBoard,
  fetchPagesForStoryBoardRequest,
} from "src/redux/actions/cleverstory";
import { useSelector } from "react-redux";
import Reveal from "src/components/animation";
import InfiniteScroll from "src/components/infiniteScroll";
import { Collection } from "src/types/collections";
import { Asset } from "src/types/assets";
import { toast } from "react-toastify";
import Toast from "src/components/toast";
import Text from "src/components/text";

const defaultAsset = '/images/defaultIcons/default_asset.jpg'

interface CleverstoryBrowserProps {
  workspace: string;
  storyboards: StoryBoard[];
  selectedAssets: (Asset | Collection)[];

  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;

}

interface StoryBoardRowProps {
  entity: any;
  width: number;
  workspace: string;
  selectedAssets: (Asset | Collection)[];
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

interface PagesProps {
  entity: any;
  expanded: boolean;
  workspace: string;
  storyboard: string;
  selectedAssets: (Asset | Collection)[];
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const Pages: React.FC<PagesProps> = ({
  entity,
  expanded,
  workspace,
  storyboard,
  selectedAssets,
  onSelection
}) => {
  const dispatch = useDispatch();

  const [selectedPageId, setSelectedPageId] = React.useState<string | null>(null);
  const assetFromStoryBoard = useSelector((state: any) =>
    _.get(state, `cleverStory.createAssetFromStoryBoard.${selectedPageId}`, {}),
    _.isEqual
  );

  useEffect(() => {
    if (assetFromStoryBoard.created && assetFromStoryBoard.item) {
      onSelection(assetFromStoryBoard.item);
    }
  }, [assetFromStoryBoard]);


  const loading = _.get(entity, "loading", false);

  if (loading && expanded) {
    return (
      <div className={styles.pages}>
        <div className={styles.pagePlaceholderContainer}>
          {_.times(3, (index) => (
            <PagePlaceholder key={index} />
          ))}
        </div>
      </div>
    );
  }

  if (_.size(entity.items) === 0 && !loading) {
    return <div className={styles.emptyMessage}>No Pages Found</div>;
  }

  const handlePageClick = (page: Page) => {
    dispatch(
      createAssetFromStoryBoard({
        workspace: workspace,
        storyboard: storyboard,
        page: page,
      }),
    );
  };


  return (
    <Reveal show={_.size(entity.items) > 0}>
      <div className={styles.storyBoardPage}>
        {_.size(entity.items) > 0 &&
          entity.items.map((page: Page, index: number) => {
            const pageIcon = _.get(page, "icon.url");
            return (
              <div
                className={styles.page}
                key={index}
                onClick={() => handlePageClick(page)}
              >
                <p className={styles.index}>
                  {(index + 1) < 10 && 0}
                  {index + 1}
                </p>

                <div className={styles.innerContainer}>
                  {pageIcon && <IKImage path={pageIcon} />}
                  {!pageIcon && <img src={defaultAsset} />}
                  <p className={styles.assetName}> {_.get(page, "name")} </p>
                  {entity?.name}
                </div>
              </div>
            );
          })}
      </div>
    </Reveal>
  );
};

const StoryBoardRow: React.FC<StoryBoardRowProps> = ({
  entity,
  width,
  onSelection,
  workspace,
  selectedAssets,
}) => {
  const dispatch = useDispatch();


  const storyBoardType = IntegraionService.getStoryBoardType(
    _.get(entity, "page.storyboardType"),
  );

  const assetAlreadyExists = _.find(selectedAssets, (item) => {
    return _.get(item, "cleverstoryPageId", "") === _.get(entity, "page.id");
  });

  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();
  const thumbnail = _.get(entity, "page.icon.url");

  const assetFromStoryBoard = useSelector((state: any) =>
    _.get(state, `cleverStory.createAssetFromStoryBoard.${_.get(entity, "page.id")}`, {}),
    _.isEqual
  );

  const createdAsset = useSelector((state: any) =>
    _.get(state, `cleverStory.create.${_.get(entity, "page.id")}`, {})
  );

  useEffect(() => {
    if (assetFromStoryBoard.created && storyBoardType === "Interactive experience") {
      onSelection(assetFromStoryBoard.item);
    }
  }, [assetFromStoryBoard]);

  useEffect(() => {
    if(createdAsset && createdAsset.error){
      toast(<Toast.Failure message={`Datasource storage is full.`} />, {
        autoClose: 100000
      })
    }
  }, [createdAsset])


  const { loading, pages, currentPage, nextPage, hasMorePages } = useSelector((state: any) => {
    const workspaceItem = (_.get(state, "cleverStory.workspaces") || []).find(item => {
      const id = _.get(item, "item.workspace");
      if (id === workspace) {
        return item;
      }
    });

    if (!workspaceItem) {
      return {
        loading: false,
        pages: [],
        currentPage: -1,
        nextPage: null,
        hasMorePages: false,
      };
    }

    const storyboardItem = (_.get(workspaceItem, "storyboards.items") || []).find(item => {
      const id = _.get(item, "item.id");
      return entity.id === id;
    });

    const loading = _.get(storyboardItem, "pages.loading", false);
    const items = (_.get(storyboardItem, "pages.items") || []);

    const pagination = _.get(workspaceItem, 'pages.pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      pages: items,
      hasMoreStoryBoards: hasMoreItems,
      nextPage: nextPage,
      currentPage: _.get(pagination, "currentPage", 0)
    };
  });

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    if (storyBoardType === "Interactive experience") {
      if (!assetAlreadyExists) {
        dispatch(
          createAssetFromStoryBoard({
            storyboard: entity.id,
            workspace: workspace,
            page: _.get(entity, "page"),
          }),
        );
      }
      
      return;
    }

    // 
    dispatch(fetchPagesForStoryBoardRequest({
      workspace: workspace,
      storyboard: entity.id,
      page: 0,
      limit: 20,
    }));

    setExpanded(!expanded);
    setMountChildren(true);
  };

  const loadMorePages = () => {
    if (!nextPage) {
      return;
    }

    dispatch(fetchPagesForStoryBoardRequest({
      workspace: workspace,
      storyboard: entity.id,
      page: nextPage,
      limit: 20,
    }));
  };

  return (
    <>
      <div
        className={cx(styles.entity, styles.storyBoard)}
        style={{ width: width }}
        onClick={handleClick}
      >
        <div className={styles.inner}>
          {thumbnail ? (
            <IKImage
              className={styles.icon}
              path={_.get(entity, "page.icon.url")}
            />
          ) : (
            <img
              src={defaultAsset}
              alt="default asset"
              className={styles.icon}
            />
          )}
          <div className={styles.details}>
            <div className={styles.innerContainer}>
              <Text className={styles.name} text={entity?.name} lines={1} />
            </div>
            <div className={styles.metaDataContainer}>
              {storyBoardType && (
                <div className={styles.meta}>{storyBoardType}</div>
              )}
              <div className={styles.date}>
                <Text text={`Last updated on : ${dateFormat(_.get(entity, "page.lastPublished"), "dd mmm yyyy")}`} />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          <button className={cx(styles.action)}>
            <i
              className={cx(styles.arrow, {
                [styles.invertedDropdown]: expanded,
                ["paperflite-add"]: storyBoardType === "Interactive experience",
                ["paperflite-dropdown"]:
                  storyBoardType !== "Interactive experience",
              })}
            ></i>
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div
            ref={containerRef}
            className={cx(styles.inner, styles.storyBoardPagesContainer)}
          >
            <InfiniteScroll
              loading={_.get(entity, "loading", false)}
              hasMore={hasMorePages}
              loadMore={loadMorePages}
            >
              <Pages
                selectedAssets={selectedAssets}
                entity={{
                  items: pages,
                  loading: loading,
                }}
                storyboard={entity.id}
                workspace={workspace}
                expanded={expanded}
                onSelection={onSelection}
              />
            </InfiniteScroll>
          </div>
        </animated.div>
      )}
    </>
  );
};

const CleverStoryBrowser: React.FC<CleverstoryBrowserProps> = ({
  storyboards,
  onSelection,
  workspace,
  selectedAssets,
}) => {
  return (
    <>
      {storyboards.map((storyBoard: StoryBoard) => (
        <StoryBoardRow
          workspace={workspace}
          entity={storyBoard}
          width={330}
          onSelection={onSelection}
          key={storyBoard.id}
          selectedAssets={selectedAssets}
        />
      ))}
    </>
  );
};

export const PagePlaceholder: React.FC<any> = () => {
  return (
    <div className={cx(styles.pagePlaceholder, styles.placeholder)}>
      <div className={cx(styles.img, styles.block)} />
    </div>
  );
};

export const StoryBoardPlaceholder: React.FC<any> = () => {
  return (
    <div className={cx(styles.entity, styles.storyBoard, styles.placeholder)}>
      <div className={styles.inner}>
        <div className={cx(styles.icon, styles.block)}></div>
        <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={styles.metaDataContainer}>
            <div className={styles.meta}>
              <span className={styles.block} /> |{" "}
              <span className={styles.block} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CleverStoryBrowser;
